// src/fees/print.js — печат на голямата таблица „Такси и плащания".
//
// ЗАЩО отделен файл: печатът е ДРУГ документ, не екранът. Матрицата е 15
// колони и на хартия трябва A4 landscape, свои размери и своя рамка — държано
// заедно с екрана, всяка промяна по единия чупеше другия. Образецът е
// src/invoices/print.js: същите esc() и printHTML() от ../protocols/print.js.
//
// ВАЖНО 1 (пари): тук НЕ се смятат и НЕ се форматират суми. Моделът идва вече
// готов от src/fees/index.js (през eur()/fromCents()), за да остане ЕДНО
// преобразуване „центове ↔ пари" за целия раздел. Иначе печатът рано или късно
// би показал друго число от екрана — точно уроците на normalizeId и на totals.
//
// ВАЖНО 2 (поверителност): това са вътрешните пари на кантората и листът излиза
// от нея. На хартията влиза САМО таблицата: клиент, подпис, счетоводител,
// месеците и дългът. Без ЕИК, без имейли, без бележки по клиента — каквото не е
// колона на екрана, не е и на печата.
//
// ВАЖНО 3 (кои колони): екранът вече крие колони по избор и печатът го УВАЖАВА —
// печата се това, което се вижда. Затова водещите колони и месеците идват като
// списъци, а не като фиксирани полета (виж feesTableHTML).

import { esc, printHTML } from '../protocols/print.js';

// Стиловете са ВГРАДЕНИ в документа: печатната страница се отваря в собствен
// iframe и не вижда styles.css на приложението.
const CSS = '@page{size:A4 landscape;margin:9mm}*{box-sizing:border-box}html,body{margin:0;background:#fff}'
  + 'body{font-family:"Segoe UI",Arial,Helvetica,sans-serif;color:#1c2733;font-size:11px;'
  + '-webkit-print-color-adjust:exact;print-color-adjust:exact;padding:6px}'
  + '.rpt{max-width:277mm;margin:0 auto}'
  + '.rhead{display:flex;justify-content:space-between;align-items:flex-end;border-bottom:2px solid #15324e;padding-bottom:8px;margin-bottom:10px}'
  + '.rt{font-size:17px;font-weight:800;color:#15324e}.rc{font-size:11px;color:#5b6b7a;margin-top:2px}'
  + '.rd{text-align:right;font-size:10px;color:#5b6b7a}.rd b{display:block;color:#1c2733;font-size:11px;margin-top:2px}'
  + 'table{width:100%;border-collapse:collapse;font-size:10px;table-layout:fixed}'
  + 'thead th{background:#15324e;color:#fff;text-align:left;padding:5px 4px;font-weight:600;font-size:9.5px}'
  + 'thead th.m{text-align:center;width:5.2%}thead th.num{text-align:right;width:8%}'
  + 'tbody td{padding:4px;border-bottom:1px solid #dbe3ea;font-variant-numeric:tabular-nums;'
  + 'overflow:hidden;text-overflow:ellipsis;white-space:nowrap}'
  + 'tbody td.m{text-align:center}tbody td.num{text-align:right;font-weight:700}'
  // Дългът е червен, но НЕ само червен: колоната си има заглавие и число —
  // цветът е допълнение, не единственият носител на смисъла.
  + 'tbody td.owes{color:#b3261e}'
  // Надплатилият е зелен, но пак не САМО зелен: до сумата стои думата „аванс".
  // Този лист често излиза черно-бял — тогава думата е единственият носител.
  + 'tbody td.over{color:#0f766e;font-weight:600}'
  + 'tbody tr:nth-child(even){background:#fafcfc}'
  + 'tfoot td,tfoot th{padding:6px 4px;font-weight:800;color:#15324e;border-top:2px solid #15324e;'
  + 'font-variant-numeric:tabular-nums}'
  + 'tfoot th{text-align:right;font-size:10px}tfoot td.m{text-align:center}tfoot td.num{text-align:right}'
  + 'tfoot tr.second td,tfoot tr.second th{border-top:1px solid #dbe3ea;font-weight:600;color:#5b6b7a}'
  + '.legend{margin-top:8px;font-size:9.5px;color:#5b6b7a}';

// „1 клиент", но „2 клиента" — числото се чете на глас от собственика.
function rowCount(n) { return n + (n === 1 ? ' клиент' : ' клиента'); }

// Датата на печата — по същия начин, както я прави печатът на фактурите.
function today() {
  const n = new Date();
  return ('0' + n.getDate()).slice(-2) + '.' + ('0' + (n.getMonth() + 1)).slice(-2) + '.' + n.getFullYear();
}

// Само сглобяване на текста — изнесено, за да може да се провери без принтер.
//
// Водещите колони идват като СПИСЪК (m.leadHeads ↔ r.lead), а не като три
// фиксирани полета: на екрана „Подпис" и „Счетоводител" се крият по избор и
// хартията трябва да ги следва. Един списък значи, че главата и редът не могат
// да се разминат — а colspan-ът на сборовете стъпва на неговата дължина.
export function feesTableHTML(m) {
  const year = String(m.year || '');
  const debtHead = m.through ? 'Дълг към ' + m.through : 'Дълг';
  const lead = (Array.isArray(m.leadHeads) && m.leadHeads.length) ? m.leadHeads : ['Клиент'];
  const leadHead = lead.map((h) => '<th>' + esc(h) + '</th>').join('');
  const months = (m.monthLabels || []).map((x) => '<th class="m">' + esc(x) + '</th>').join('');

  const body = (m.rows || []).map((r) => '<tr>'
    + (r.lead || []).map((v) => '<td>' + esc(v) + '</td>').join('')
    + (r.months || []).map((c) => '<td class="m">' + esc(c.text) + (c.mark ? ' ' + esc(c.mark) : '') + '</td>').join('')
    + '<td class="num' + (r.owes ? ' owes' : (r.over ? ' over' : '')) + '">' + esc(r.debt) + '</td>'
    + '</tr>').join('');

  // Филтриран изглед: и сборовете са само за показаните редове. Листът трябва
  // да го КАЖЕ — иначе тръгва по хората като пълна справка и числата лъжат.
  const f = m.filtered;
  const sums = (m.sums || {});
  const sumRow = (cls, label, cells) => '<tr' + (cls ? ' class="' + cls + '"' : '') + '>'
    + '<th colspan="' + lead.length + '">' + esc(label + (f ? ' (филтрирани)' : '')) + '</th>'
    + (cells || []).map((c) => '<td class="m">' + esc(c) + '</td>').join('')
    + '<td></td></tr>';

  // Ред със сбор за КОЛОНАТА „Дълг": надписът минава през всички колони преди
  // нея, а числото застава точно под нея. Два отделни реда, а не един сбор,
  // защото „дължимо" и „надплатено" НЕ се изваждат едно от друго — това беше
  // цялото оплакване („на печата все още ми излизат надплатените").
  const debtSpan = lead.length + (m.monthLabels || []).length;
  const debtRow = (cls, label, value) => '<tr' + (cls ? ' class="' + cls + '"' : '') + '>'
    + '<th colspan="' + debtSpan + '">' + esc(label + (f ? ' (филтрирани)' : '')) + '</th>'
    + '<td class="num">' + esc(value) + '</td></tr>';

  return '<!DOCTYPE html><html lang="bg"><head><meta charset="utf-8">'
    + '<title>Такси и плащания — ' + esc(year) + '</title><style>' + CSS + '</style></head><body>'
    + '<div class="rpt">'
    + '<div class="rhead">'
    + '<div><div class="rt">Такси и плащания — ' + esc(year) + ' г.</div>'
    // Периодът е ПЪРВОТО нещо на листа: хартията тръгва по хората и „Дълг
    // 115,00" без месец се чете като „за цялата година". Липсва ли месецът, НЕ
    // се измисля — пише само, че числата са към момента.
    + '<div class="rc">' + esc(m.through
      ? 'Данните са до ' + m.through + ' ' + year + ' г. включително'
      : 'Данните са към момента')
    + ' · суми в евро · ' + rowCount((m.rows || []).length)
    + (f ? esc(' · ФИЛТРИРАН изглед: показани ' + f.shown + ' от ' + f.total + ' — сборовете са само за тях') : '')
    + '</div></div>'
    + '<div class="rd">Отпечатано на<b>' + today() + ' г.</b></div></div>'
    + '<table><thead><tr>'
    + leadHead + months
    + '<th class="num">' + esc(debtHead) + '</th>'
    + '</tr></thead>'
    + '<tbody>' + body + '</tbody>'
    + '<tfoot>'
    + sumRow('', 'Платено по месеци', sums.paid)
    + sumRow('second', 'Начислено по месеци', sums.expected)
    // „Общо дължимо" е сборът само на ПОЛОЖИТЕЛНИТЕ дългове. Редът за
    // надплатеното излиза само когато има такива — иначе е „0,00 €", което на
    // лист „Задължения" изглежда като данни.
    + (sums.receivable != null ? debtRow('', 'Общо дължимо', sums.receivable) : '')
    + (sums.overpaid ? debtRow('second', 'Надплатено (аванс, не е вземане)', sums.overpaid) : '')
    + '</tfoot></table>'
    + '<div class="legend">✻ месец с активна ДДС такса (E) · · месец извън периода на клиента'
    + ' · „аванс" = клиентът е надплатил; сумата НЕ се вади от общото дължимо'
    // Целогодишните числа на сървъра (с годишните приключвания) влизат само
    // когато казват нещо различно от отпечатаното — иначе са повторение.
    + (sums.whole
      ? esc(' · за цялата година, включително годишните приключвания: дължимо ' + sums.whole.receivable
        + ', надплатено ' + sums.whole.overpaid)
      : '')
    + '</div>'
    + '</div></body></html>';
}

export function printFeesTable(model) {
  printHTML(feesTableHTML(model));
}
